<template>
  <div class="admin desktop mb-10">
    <LinkedinDialog
      :dialog="dialog"
      @close="dialog = false"
      :accounts="linkedin_accounts"
      @add="addAccounts"
    />

    <!-- Import Posts Dialog -->
    <v-dialog v-model="importDialog" width="900" scrollable>
      <v-card flat class="shadcn-card">
        <!-- Header -->
        <div class="d-flex align-center justify-space-between pa-5 pb-0">
          <div>
            <h3 style="font-size: 14px; font-weight: 600; color: #09090b">
              Import Posts
            </h3>
            <p class="shadcn-muted mt-1">
              Select posts from your LinkedIn feed to import.
            </p>
          </div>
          <v-btn variant="text" icon size="small" @click="importDialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Category selector -->
        <div class="px-5 pt-4 pb-2">
          <label class="shadcn-label">Category</label>
          <v-select
            v-model="importCategory"
            variant="outlined"
            density="compact"
            class="shadcn-input"
            placeholder="Uncategorized"
            :items="categoryOptions"
            item-title="name"
            item-value="uid"
            hide-details
            clearable
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <template v-slot:prepend v-if="item.raw.color">
                  <v-icon size="12" :color="item.raw.color">mdi-circle</v-icon>
                </template>
              </v-list-item>
            </template>
          </v-select>
        </div>

        <v-divider class="shadcn-divider mx-5 mt-3"></v-divider>

        <!-- Select all toggle -->
        <div class="d-flex align-center justify-space-between px-5 py-3">
          <span class="shadcn-muted">
            {{ selectedPosts.length }} of {{ feedPosts.length }} posts selected
          </span>
          <v-btn
            variant="text"
            size="small"
            style="
              text-transform: none;
              letter-spacing: 0;
              font-size: 10pt;
              font-weight: 500;
            "
            @click="toggleSelectAll"
          >
            {{
              selectedPosts.length === feedPosts.length
                ? "Deselect all"
                : "Select all"
            }}
          </v-btn>
        </div>

        <v-divider class="shadcn-divider mx-5"></v-divider>

        <!-- Posts list -->
        <v-card-text style="max-height: 60vh; overflow-y: auto" class="pa-0">
          <div
            v-if="feedPosts.length === 0"
            class="d-flex align-center justify-center pa-10"
          >
            <span class="shadcn-muted">No posts found in your feed.</span>
          </div>
          <div v-else>
            <div
              v-for="post in feedPosts"
              :key="post.id"
              class="import-post-row d-flex align-start px-5 py-3"
              :class="{ 'import-post-row--selected': isPostSelected(post) }"
              style="cursor: pointer"
              @click="togglePostSelection(post)"
            >
              <v-checkbox
                :model-value="isPostSelected(post)"
                hide-details
                density="compact"
                class="mt-0 pt-0 mr-3"
                style="flex: none"
                @click.stop="togglePostSelection(post)"
              ></v-checkbox>
              <div style="flex: 1; min-width: 0">
                <p class="import-post-text">
                  {{ formatCommentary(post.commentary) }}
                </p>
                <div class="d-flex align-center mt-1" style="gap: 8px">
                  <span class="shadcn-muted" style="font-size: 9pt">
                    {{ formatDate(post.publishedAt) }}
                  </span>
                  <span
                    v-if="post.content?.media?.length"
                    class="shadcn-muted"
                    style="font-size: 9pt"
                  >
                    <v-icon size="12" class="mr-1">mdi-image-outline</v-icon
                    >{{ post.content.media.length }} media
                  </span>
                  <span
                    v-if="post.content?.article"
                    class="shadcn-muted"
                    style="font-size: 9pt"
                  >
                    <v-icon size="12" class="mr-1">mdi-link-variant</v-icon
                    >Article
                  </span>
                </div>
              </div>
              <v-img
                v-if="getPostThumbnail(post)"
                :src="getPostThumbnail(post)"
                min-width="200"
                width="200"
                height="120"
                class="rounded ml-3"
                style="flex: none"
                cover
              ></v-img>
            </div>
            <div v-if="hasMorePosts" class="d-flex justify-center py-4">
              <v-btn
                variant="outlined"
                size="small"
                class="shadcn-btn-outline"
                :loading="loadingMore"
                @click="loadMorePosts"
              >
                <v-icon size="15" class="mr-1">mdi-refresh</v-icon>
                Load more posts
              </v-btn>
            </div>
          </div>
        </v-card-text>

        <v-divider class="shadcn-divider"></v-divider>

        <!-- Footer -->
        <div class="d-flex align-center justify-space-between pa-5">
          <v-btn
            variant="outlined"
            size="small"
            class="shadcn-btn-outline"
            @click="importDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            variant="flat"
            color="#18181b"
            size="small"
            class="shadcn-btn-primary"
            :disabled="selectedPosts.length === 0"
            :loading="importing"
            @click="importSelectedPosts"
          >
            Import {{ selectedPosts.length }} post{{
              selectedPosts.length !== 1 ? "s" : ""
            }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Header -->
    <div
      class="d-flex align-center justify-space-between mb-6"
      style="max-width: 800px"
    >
      <h1>About {{ brand?.name }}</h1>
      <div class="d-flex align-center" style="gap: 10px">
        <v-btn
          :loading="analyzing_brand"
          :disabled="!brand?.website || accounts.length === 0"
          variant="outlined"
          size="small"
          class="shadcn-btn-outline"
          @click="analyzeBrand"
        >
          <v-icon size="15" class="mr-2">mdi-creation</v-icon>
          Generate brand details
        </v-btn>
        <v-btn
          variant="flat"
          color="#18181b"
          size="small"
          class="shadcn-btn-primary"
          @click="save"
          >Save</v-btn
        >
      </div>
    </div>

    <!-- Tabbed content -->
    <div v-if="brand">
      <v-card flat class="shadcn-card" style="max-width: 800px">
        <v-tabs v-model="tab" color="#18181b" density="compact">
          <v-tab value="general" class="brand-tab">General</v-tab>
          <v-tab
            value="branding"
            class="brand-tab"
            :disabled="accounts.length === 0"
            >Branding</v-tab
          >
          <v-tab value="voice" class="brand-tab">Voice</v-tab>
          <v-tab value="icp" class="brand-tab">ICP</v-tab>
          <v-tab value="positioning" class="brand-tab">Positioning</v-tab>
        </v-tabs>

        <v-divider></v-divider>

        <v-tabs-window v-model="tab">
          <!-- ==================== GENERAL TAB ==================== -->
          <v-tabs-window-item value="general">
            <div class="pa-6">
              <div
                style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px"
                class="mb-6"
              >
                <div>
                  <label class="shadcn-label">Name</label>
                  <v-text-field
                    placeholder="Name"
                    variant="outlined"
                    v-model="brand.name"
                    hide-details
                    density="compact"
                    class="shadcn-input"
                  ></v-text-field>
                </div>
                <div>
                  <label class="shadcn-label">Website</label>
                  <v-text-field
                    placeholder="Your brand's website"
                    variant="outlined"
                    v-model="brand.website"
                    hide-details
                    density="compact"
                    class="shadcn-input"
                  ></v-text-field>
                </div>
              </div>

              <v-divider class="shadcn-divider mb-6"></v-divider>

              <!-- LinkedIn connection -->
              <div>
                <label class="shadcn-label mb-3">LinkedIn Account</label>

                <div
                  v-if="accounts.length === 0"
                  class="d-flex align-center"
                  style="gap: 10px"
                >
                  <span class="shadcn-muted"
                    >Connect your LinkedIn account</span
                  >
                  <v-btn
                    v-if="userProfile?.linkedin?.organizations?.length === 0"
                    variant="outlined"
                    size="small"
                    class="shadcn-btn-outline"
                    @click="connectLinkedin"
                    >Connect</v-btn
                  >
                  <v-menu
                    v-if="
                      userProfile?.linkedin &&
                      userProfile?.linkedin?.organizations?.length > 0
                    "
                  >
                    <template v-slot:activator="{ props }">
                      <v-btn
                        class="shadcn-btn-outline"
                        size="small"
                        variant="outlined"
                        v-bind="props"
                      >
                        Select account
                        <v-icon size="small" class="ml-2"
                          >mdi-chevron-down</v-icon
                        >
                      </v-btn>
                    </template>
                    <v-card class="shadcn-card">
                      <v-list>
                        <v-list-item
                          v-for="a in userProfile.linkedin.organizations.sort(
                            (a, b) => a.name.localeCompare(b.name),
                          )"
                          :key="a.id"
                          @click="selectAccount(a)"
                        >
                          {{ a.name }}
                        </v-list-item>
                      </v-list>
                    </v-card>
                  </v-menu>
                </div>

                <div
                  v-else-if="
                    accounts.length > 0 && !accounts[0]?.access_token_valid
                  "
                  class="d-flex align-center"
                  style="gap: 10px"
                >
                  <span class="shadcn-muted"
                    >Your account is disconnected!</span
                  >
                  <v-btn
                    variant="outlined"
                    size="small"
                    class="shadcn-btn-outline"
                    @click="connectLinkedin"
                    >Reconnect</v-btn
                  >
                </div>

                <div v-else>
                  <div v-for="account in accounts" :key="account.id">
                    <div
                      class="d-flex align-center justify-space-between pa-3"
                      style="
                        border: 1px solid hsl(214.3 31.8% 91.4%);
                        border-radius: 8px;
                      "
                    >
                      <div class="d-flex align-center" style="gap: 10px">
                        <v-avatar
                          size="28"
                          :image="account.profile_picture"
                        ></v-avatar>
                        <span style="font-size: 10pt">{{ account.name }}</span>
                      </div>
                      <div class="d-flex align-center" style="gap: 8px">
                        <v-btn
                          @click="importDialog = true"
                          variant="outlined"
                          size="small"
                          class="shadcn-btn-outline"
                        >
                          <v-icon size="15" class="mr-2">mdi-import</v-icon>
                          Import posts
                        </v-btn>
                        <v-btn
                          variant="text"
                          icon
                          size="small"
                          @click="deleteAccount(account.id)"
                        >
                          <v-icon size="15">mdi-delete-outline</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-tabs-window-item>

          <!-- ==================== BRANDING TAB ==================== -->
          <v-tabs-window-item value="branding">
            <div class="pa-6">
              <!-- Logos -->
              <label class="shadcn-label mb-3">Logos</label>
              <div class="d-flex mb-6" style="gap: 20px; flex-wrap: wrap">
                <div>
                  <span class="shadcn-muted">Dark</span>
                  <v-card
                    width="100"
                    height="100"
                    class="d-flex align-center justify-center pa-4 mt-1 shadcn-card"
                  >
                    <v-img
                      contain
                      :src="branding?.logo?.dark"
                      alt="Brand logo"
                      @click="$refs.darkLogoRef.click()"
                      style="cursor: pointer"
                      v-if="branding?.logo?.dark"
                    />
                    <v-btn
                      v-else
                      icon="mdi-plus"
                      variant="text"
                      @click="$refs.darkLogoRef.click()"
                    ></v-btn>
                    <input
                      ref="darkLogoRef"
                      type="file"
                      multiple
                      @change="onDarkLogoChange"
                      accept="image/*"
                    />
                  </v-card>
                </div>
                <div>
                  <span class="shadcn-muted">Light</span>
                  <v-card
                    width="100"
                    height="100"
                    class="d-flex align-center justify-center pa-4 bg-primary mt-1 shadcn-card"
                  >
                    <v-img
                      contain
                      :src="branding?.logo?.light"
                      alt="Brand logo"
                      @click="$refs.lightLogoRef.click()"
                      style="cursor: pointer"
                      v-if="branding?.logo?.light"
                    />
                    <v-btn
                      v-else
                      icon="mdi-plus"
                      variant="text"
                      @click="$refs.lightLogoRef.click()"
                    ></v-btn>
                    <input
                      ref="lightLogoRef"
                      type="file"
                      multiple
                      @change="onLightLogoChange"
                      accept="image/*"
                    />
                  </v-card>
                </div>
              </div>

              <v-divider class="shadcn-divider my-6"></v-divider>

              <!-- Colors -->
              <label class="shadcn-label mb-3">Colors</label>
              <div class="d-flex" style="gap: 20px; flex-wrap: wrap">
                <div>
                  <span class="shadcn-muted">Primary</span>
                  <v-card
                    width="80"
                    height="80"
                    class="d-flex align-center justify-center pa-3 mt-1 shadcn-card"
                    :style="{ backgroundColor: branding?.color?.primary }"
                  >
                    <ColorPickerBrand @color="setPrimary" />
                  </v-card>
                </div>
                <div>
                  <span class="shadcn-muted">Background</span>
                  <v-card
                    width="80"
                    height="80"
                    class="d-flex align-center justify-center pa-3 mt-1 shadcn-card"
                    :style="{ backgroundColor: branding?.color?.background }"
                  >
                    <ColorPickerBrand @color="setBackground" />
                  </v-card>
                </div>
                <div>
                  <span class="shadcn-muted">Button</span>
                  <v-card
                    width="80"
                    height="80"
                    class="d-flex align-center justify-center pa-3 mt-1 shadcn-card"
                    :style="{ backgroundColor: branding?.color?.button }"
                  >
                    <ColorPickerBrand @color="setButton" />
                  </v-card>
                </div>
                <div>
                  <span class="shadcn-muted">Text</span>
                  <v-card
                    width="80"
                    height="80"
                    class="d-flex align-center justify-center pa-3 mt-1 shadcn-card"
                    :style="{ backgroundColor: branding?.color?.text }"
                  >
                    <ColorPickerBrand @color="setText" />
                  </v-card>
                </div>
                <div>
                  <span class="shadcn-muted">Accent</span>
                  <v-card
                    width="80"
                    height="80"
                    class="d-flex align-center justify-center pa-3 mt-1 shadcn-card"
                    :style="{ backgroundColor: branding?.color?.accent }"
                  >
                    <ColorPickerBrand @color="setAccent" />
                  </v-card>
                </div>
              </div>
            </div>
          </v-tabs-window-item>

          <!-- ==================== VOICE TAB ==================== -->
          <v-tabs-window-item value="voice">
            <div class="pa-6">
              <!-- Voice description -->
              <div class="mb-6">
                <label class="shadcn-label">Brand Voice</label>
                <p class="shadcn-muted mb-3">
                  Describe your brand's tone, personality, and writing style.
                  This guides how AI generates content for you.
                </p>
                <v-textarea
                  v-model="brand.voice"
                  variant="outlined"
                  rows="6"
                  hide-details
                  density="compact"
                  class="shadcn-textarea"
                  placeholder="e.g., Professional but approachable. We use simple language, avoid jargon, and speak directly to our audience..."
                ></v-textarea>
              </div>

              <!-- Language -->
              <div class="mb-6">
                <label class="shadcn-label">Language</label>
                <v-autocomplete
                  v-model="brand.language"
                  variant="outlined"
                  density="compact"
                  class="shadcn-input"
                  placeholder="Select language"
                  :items="languages"
                  item-title="name"
                  item-value="name"
                  hide-details
                ></v-autocomplete>
              </div>

              <v-divider class="shadcn-divider my-6"></v-divider>

              <!-- Example Posts -->
              <div>
                <label class="shadcn-label">Example Posts</label>
                <p class="shadcn-muted mb-3">
                  Paste posts that match the writing style you want. The AI will
                  use these as reference when generating new content.
                </p>

                <!-- Add new example -->
                <div class="mb-4">
                  <v-textarea
                    v-model="new_voice_example"
                    variant="outlined"
                    rows="4"
                    hide-details
                    density="compact"
                    class="shadcn-textarea"
                    placeholder="Paste a post example here..."
                  ></v-textarea>
                  <div class="d-flex justify-end mt-2">
                    <v-btn
                      variant="outlined"
                      size="small"
                      class="shadcn-btn-outline"
                      :disabled="!new_voice_example?.trim()"
                      @click="addVoiceExample"
                    >
                      <v-icon size="15" class="mr-1">mdi-plus</v-icon>
                      Add example
                    </v-btn>
                  </div>
                </div>

                <!-- Saved examples list -->
                <div
                  v-if="voiceExamples.length > 0"
                  class="d-flex flex-column"
                  style="gap: 12px"
                >
                  <div
                    v-for="(example, index) in voiceExamples"
                    :key="index"
                    class="shadcn-example-card"
                  >
                    <div
                      class="d-flex justify-space-between align-start"
                      style="gap: 12px"
                    >
                      <div
                        style="
                          flex: 1;
                          white-space: pre-wrap;
                          word-break: break-word;
                        "
                      >
                        {{ example }}
                      </div>
                      <v-btn
                        variant="text"
                        icon
                        size="x-small"
                        @click="removeVoiceExample(index)"
                        style="flex-shrink: 0"
                      >
                        <v-icon size="14">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </div>
                <div
                  v-else
                  class="d-flex align-center justify-center pa-8"
                  style="
                    border: 1px dashed hsl(214.3 31.8% 91.4%);
                    border-radius: 8px;
                  "
                >
                  <span class="shadcn-muted"
                    >No examples added yet. Paste a post above to get
                    started.</span
                  >
                </div>
              </div>
            </div>
          </v-tabs-window-item>

          <!-- ==================== ICP TAB ==================== -->
          <v-tabs-window-item value="icp">
            <div class="pa-6">
              <label class="shadcn-label">Company Size</label>
              <p class="shadcn-muted mb-3">
                Describe your ideal customer's company size.
              </p>
              <v-textarea
                v-model="brand.ICP.company_size"
                variant="outlined"
                rows="3"
                class="shadcn-textarea mb-6"
                hide-details
                density="compact"
                placeholder="e.g., Mid-market companies with 50-500 employees..."
              ></v-textarea>

              <div
                style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px"
                class="mb-6"
              >
                <!-- Industry -->
                <div>
                  <label class="shadcn-label mb-2">Industry</label>
                  <div class="d-flex flex-column" style="gap: 8px">
                    <div
                      v-for="(item, index) in brand.ICP.industry"
                      :key="'ind-' + index"
                    >
                      <v-text-field
                        v-model="brand.ICP.industry[index]"
                        variant="outlined"
                        hide-details
                        density="compact"
                        class="shadcn-input"
                      >
                        <template v-slot:append-inner>
                          <v-btn
                            variant="text"
                            size="x-small"
                            icon
                            @click="brand.ICP.industry.splice(index, 1)"
                          >
                            <v-icon size="14">mdi-close</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </div>
                    <v-text-field
                      v-model="new_industry"
                      variant="outlined"
                      hide-details
                      density="compact"
                      class="shadcn-input"
                      placeholder="Add industry"
                      @keyup.enter="addIndustry"
                    >
                      <template v-slot:append-inner>
                        <v-btn
                          variant="text"
                          size="x-small"
                          icon
                          color="primary"
                          @click="addIndustry"
                        >
                          <v-icon size="14">mdi-plus</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                </div>

                <!-- Roles -->
                <div>
                  <label class="shadcn-label mb-2">Roles</label>
                  <div class="d-flex flex-column" style="gap: 8px">
                    <div
                      v-for="(item, index) in brand.ICP.roles"
                      :key="'role-' + index"
                    >
                      <v-text-field
                        v-model="brand.ICP.roles[index]"
                        variant="outlined"
                        hide-details
                        density="compact"
                        class="shadcn-input"
                      >
                        <template v-slot:append-inner>
                          <v-btn
                            variant="text"
                            size="x-small"
                            icon
                            @click="brand.ICP.roles.splice(index, 1)"
                          >
                            <v-icon size="14">mdi-close</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </div>
                    <v-text-field
                      v-model="new_role"
                      variant="outlined"
                      hide-details
                      density="compact"
                      class="shadcn-input"
                      placeholder="Add role"
                      @keyup.enter="addRole"
                    >
                      <template v-slot:append-inner>
                        <v-btn
                          variant="text"
                          size="x-small"
                          icon
                          color="primary"
                          @click="addRole"
                        >
                          <v-icon size="14">mdi-plus</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                </div>
              </div>

              <!-- Pain Points -->
              <div>
                <label class="shadcn-label mb-2">Pain Points</label>
                <div class="d-flex flex-column" style="gap: 8px">
                  <div
                    v-for="(item, index) in brand.ICP.painPoints"
                    :key="'pain-' + index"
                  >
                    <v-text-field
                      v-model="brand.ICP.painPoints[index]"
                      variant="outlined"
                      hide-details
                      density="compact"
                      class="shadcn-input"
                    >
                      <template v-slot:append-inner>
                        <v-btn
                          variant="text"
                          size="x-small"
                          icon
                          @click="brand.ICP.painPoints.splice(index, 1)"
                        >
                          <v-icon size="14">mdi-close</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                  <v-text-field
                    v-model="new_painPoint"
                    variant="outlined"
                    hide-details
                    density="compact"
                    class="shadcn-input"
                    placeholder="Add pain point"
                    @keyup.enter="addPainPoint"
                  >
                    <template v-slot:append-inner>
                      <v-btn
                        variant="text"
                        size="x-small"
                        icon
                        color="primary"
                        @click="addPainPoint"
                      >
                        <v-icon size="14">mdi-plus</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </div>
              </div>
            </div>
          </v-tabs-window-item>

          <!-- ==================== POSITIONING TAB ==================== -->
          <v-tabs-window-item value="positioning">
            <div class="pa-6">
              <!-- Golden Circle -->
              <div class="mb-8">
                <label class="shadcn-label" style="font-size: 12pt"
                  >Golden Circle</label
                >
                <p class="shadcn-muted mb-4">
                  Define your brand's purpose, approach, and offering.
                </p>

                <div class="mb-5">
                  <label class="shadcn-label"
                    ><b>Why</b> &mdash; Purpose & belief</label
                  >
                  <v-textarea
                    placeholder="Why does your company exist?"
                    variant="outlined"
                    v-model="brand.goldenCircle.why"
                    hide-details
                    rows="3"
                    density="compact"
                    class="shadcn-textarea"
                  ></v-textarea>
                </div>

                <div class="mb-5">
                  <label class="shadcn-label"
                    ><b>How</b> &mdash; Unique approach</label
                  >
                  <div class="d-flex flex-column" style="gap: 8px">
                    <div
                      v-for="(item, index) in brand.goldenCircle.how"
                      :key="'how-' + index"
                    >
                      <v-text-field
                        v-model="brand.goldenCircle.how[index]"
                        variant="outlined"
                        hide-details
                        density="compact"
                        class="shadcn-input"
                      >
                        <template v-slot:append-inner>
                          <v-btn
                            variant="text"
                            size="x-small"
                            icon
                            @click="brand.goldenCircle.how.splice(index, 1)"
                          >
                            <v-icon size="14">mdi-close</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </div>
                    <v-text-field
                      v-model="new_how"
                      variant="outlined"
                      hide-details
                      density="compact"
                      class="shadcn-input"
                      placeholder="How do you help your customers?"
                      @keyup.enter="addHow"
                    >
                      <template v-slot:append-inner>
                        <v-btn
                          variant="text"
                          size="x-small"
                          icon
                          color="primary"
                          @click="addHow"
                        >
                          <v-icon size="14">mdi-plus</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                </div>

                <div>
                  <label class="shadcn-label"
                    ><b>What</b> &mdash; Tangible offering</label
                  >
                  <div class="d-flex flex-column" style="gap: 8px">
                    <div
                      v-for="(item, index) in brand.goldenCircle.what"
                      :key="'what-' + index"
                    >
                      <v-text-field
                        v-model="brand.goldenCircle.what[index]"
                        variant="outlined"
                        hide-details
                        density="compact"
                        class="shadcn-input"
                      >
                        <template v-slot:append-inner>
                          <v-btn
                            variant="text"
                            size="x-small"
                            icon
                            @click="brand.goldenCircle.what.splice(index, 1)"
                          >
                            <v-icon size="14">mdi-close</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                    </div>
                    <v-text-field
                      v-model="new_what"
                      variant="outlined"
                      hide-details
                      density="compact"
                      class="shadcn-input"
                      placeholder="What do you offer to your customers?"
                      @keyup.enter="addWhat"
                    >
                      <template v-slot:append-inner>
                        <v-btn
                          variant="text"
                          size="x-small"
                          icon
                          color="primary"
                          @click="addWhat"
                        >
                          <v-icon size="14">mdi-plus</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                </div>
              </div>

              <v-divider class="shadcn-divider my-6"></v-divider>

              <!-- Products & Services -->
              <div class="mb-8">
                <label class="shadcn-label" style="font-size: 12pt"
                  >Products & Services</label
                >
                <p class="shadcn-muted mb-3">
                  What products and services do you offer?
                </p>
                <div class="d-flex flex-column" style="gap: 8px">
                  <div
                    v-for="(product, index) in brand.products_services || []"
                    :key="'prod-' + index"
                  >
                    <v-text-field
                      v-model="brand.products_services[index]"
                      variant="outlined"
                      hide-details
                      density="compact"
                      class="shadcn-input"
                    >
                      <template v-slot:append-inner>
                        <v-btn
                          variant="text"
                          size="x-small"
                          icon
                          @click="brand.products_services.splice(index, 1)"
                        >
                          <v-icon size="14">mdi-close</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                  <v-text-field
                    v-model="new_product_services"
                    variant="outlined"
                    hide-details
                    density="compact"
                    class="shadcn-input"
                    placeholder="Add a product or service"
                    @keyup.enter="addProductService"
                  >
                    <template v-slot:append-inner>
                      <v-btn
                        variant="text"
                        size="x-small"
                        icon
                        color="primary"
                        @click="addProductService"
                      >
                        <v-icon size="14">mdi-plus</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </div>
              </div>

              <v-divider class="shadcn-divider my-6"></v-divider>

              <!-- USPs -->
              <div class="mb-8">
                <label class="shadcn-label" style="font-size: 12pt"
                  >Unique Selling Points</label
                >
                <p class="shadcn-muted mb-3">
                  What makes you stand out from competitors?
                </p>
                <div class="d-flex flex-column" style="gap: 8px">
                  <div
                    v-for="(usp, index) in brand.USP || []"
                    :key="'usp-' + index"
                  >
                    <v-text-field
                      v-model="brand.USP[index]"
                      variant="outlined"
                      hide-details
                      density="compact"
                      class="shadcn-input"
                    >
                      <template v-slot:append-inner>
                        <v-btn
                          variant="text"
                          size="x-small"
                          icon
                          @click="brand.USP.splice(index, 1)"
                        >
                          <v-icon size="14">mdi-close</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                  <v-text-field
                    v-model="new_usp"
                    variant="outlined"
                    hide-details
                    density="compact"
                    class="shadcn-input"
                    placeholder="Add a unique selling point"
                    @keyup.enter="addUsp"
                  >
                    <template v-slot:append-inner>
                      <v-btn
                        variant="text"
                        size="x-small"
                        icon
                        color="primary"
                        @click="addUsp"
                      >
                        <v-icon size="14">mdi-plus</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </div>
              </div>

              <v-divider class="shadcn-divider my-6"></v-divider>

              <!-- Common Rejections -->
              <div class="mb-8">
                <label class="shadcn-label" style="font-size: 12pt"
                  >Common Rejections</label
                >
                <p class="shadcn-muted mb-3">
                  What objections do you hear from prospects?
                </p>
                <div class="d-flex flex-column" style="gap: 8px">
                  <div
                    v-for="(rejection, index) in brand.common_rejections || []"
                    :key="'rej-' + index"
                  >
                    <v-text-field
                      v-model="brand.common_rejections[index]"
                      variant="outlined"
                      hide-details
                      density="compact"
                      class="shadcn-input"
                    >
                      <template v-slot:append-inner>
                        <v-btn
                          variant="text"
                          size="x-small"
                          icon
                          @click="brand.common_rejections.splice(index, 1)"
                        >
                          <v-icon size="14">mdi-close</v-icon>
                        </v-btn>
                      </template>
                    </v-text-field>
                  </div>
                  <v-text-field
                    v-model="new_rejection"
                    variant="outlined"
                    hide-details
                    density="compact"
                    class="shadcn-input"
                    placeholder="Add a common rejection"
                    @keyup.enter="addRejection"
                  >
                    <template v-slot:append-inner>
                      <v-btn
                        variant="text"
                        size="x-small"
                        icon
                        color="primary"
                        @click="addRejection"
                      >
                        <v-icon size="14">mdi-plus</v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                </div>
              </div>

              <v-divider class="shadcn-divider my-6"></v-divider>

              <!-- What We Don't Do -->
              <div>
                <label class="shadcn-label" style="font-size: 12pt"
                  >What We Don't Do</label
                >
                <p class="shadcn-muted mb-3">
                  Help the AI understand what topics and services to avoid when
                  generating content.
                </p>
                <v-textarea
                  v-model="brand.what_we_dont_do"
                  placeholder="e.g., We don't provide financial advice, we don't sell physical products, we don't offer consulting services..."
                  variant="outlined"
                  rows="4"
                  hide-details
                  density="compact"
                  class="shadcn-textarea"
                ></v-textarea>
              </div>
            </div>
          </v-tabs-window-item>
        </v-tabs-window>
      </v-card>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { languages } from "@/mixins/languages";
import { httpsCallable } from "firebase/functions";
import { functions, db } from "@/firebase";
import { updateDoc, doc } from "firebase/firestore";
import { onboarding } from "@/mixins/onboarding";
import LinkedinDialog from "@/components/linkedin/LinkedinDialog.vue";
import ColorPickerBrand from "@/components/ColorPickerBrand.vue";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

export default {
  mixins: [languages, onboarding],
  components: {
    LinkedinDialog,
    ColorPickerBrand,
  },

  computed: {
    ...mapState({
      userProfile: (state) => state.users.userProfile,
      workspace: (state) => state.workspace.workspace,
      loading: (state) => state.loading,
      workspaces: (state) => state.workspace.workspaces,
      accounts: (state) => state.accounts.accounts,
    }),
    branding: {
      get() {
        return this.$store.state.workspace.branding;
      },
      set(value) {
        this.$store.commit("setBranding", value);
      },
    },
    brand: {
      get() {
        return this.$store.state.workspace.brand;
      },
      set(value) {
        this.$store.commit("setBrand", value);
      },
    },
    voiceExamples() {
      return this.brand?.voice_examples || [];
    },
    categories() {
      return this.$store.state.posts.categories || [];
    },
    categoryOptions() {
      return this.categories.filter((c) => c.status !== "suggested");
    },
    feedPosts() {
      return this.accounts?.[0]?.feed?.elements || [];
    },
    hasMorePosts() {
      const paging = this.accounts?.[0]?.feed?.paging;
      return paging?.links?.some((l) => l.rel === "next") || false;
    },
  },

  data() {
    return {
      tab: "general",
      analyzing_colors: false,
      changes: false,
      generating_voice: false,
      new_industry: "",
      new_role: "",
      new_painPoint: "",
      new_how: "",
      new_what: "",
      new_product_services: "",
      new_usp: "",
      new_rejection: "",
      new_voice_example: "",
      importDialog: false,
      selectedPosts: [],
      importCategory: null,
      importing: false,
      loadingMore: false,
    };
  },

  methods: {
    addVoiceExample() {
      if (!this.new_voice_example?.trim()) return;
      if (!this.brand.voice_examples) {
        this.brand.voice_examples = [];
      }
      this.brand.voice_examples.push(this.new_voice_example.trim());
      this.new_voice_example = "";
    },
    removeVoiceExample(index) {
      this.brand.voice_examples.splice(index, 1);
    },
    isPostSelected(post) {
      return this.selectedPosts.some((p) => p.id === post.id);
    },
    togglePostSelection(post) {
      const idx = this.selectedPosts.findIndex((p) => p.id === post.id);
      if (idx >= 0) {
        this.selectedPosts.splice(idx, 1);
      } else {
        this.selectedPosts.push(post);
      }
    },
    toggleSelectAll() {
      if (this.selectedPosts.length === this.feedPosts.length) {
        this.selectedPosts = [];
      } else {
        this.selectedPosts = [...this.feedPosts];
      }
    },
    formatCommentary(text) {
      if (!text) return "";
      return text.replace(/\{hashtag\|\\"?#\|([^}]+)\}/g, "#$1");
    },
    formatDate(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    getPostThumbnail(post) {
      if (post.content?.media?.[0]?.src?.src)
        return post.content.media[0].src.src;
      if (post.content?.media?.[0]?.src) return post.content.media[0].src;
      if (post.content?.article?.src?.src) return post.content.article.src.src;
      return null;
    },
    async loadMorePosts() {
      if (!this.accounts?.[0] || this.loadingMore) return;
      this.loadingMore = true;
      try {
        await this.$store.dispatch("loadMore", this.accounts[0]);
      } catch (error) {
        console.error(error);
        this.$store.commit("setNotification", {
          show: true,
          text: "Failed to load more posts.",
          type: "error",
          icon: "",
        });
      } finally {
        this.loadingMore = false;
      }
    },
    async importSelectedPosts() {
      this.importing = true;
      try {
        const importPosts = httpsCallable(functions, "importPosts", {
          timeout: 300000,
        });
        await importPosts({
          category: {
            uid: this.importCategory || null,
            based_on: this.selectedPosts,
          },
          workspace: this.workspace,
        });
        this.$store.commit("setNotification", {
          show: true,
          text: `${this.selectedPosts.length} post${this.selectedPosts.length !== 1 ? "s" : ""} imported successfully`,
          type: "primary",
          icon: "",
        });
        this.$store.dispatch("fetchPosts");
        this.importDialog = false;
        this.selectedPosts = [];
        this.importCategory = null;
      } catch (error) {
        console.error(error);
        this.$store.commit("setNotification", {
          show: true,
          text: "Failed to import posts. Please try again.",
          type: "error",
          icon: "",
        });
      } finally {
        this.importing = false;
      }
    },
    addIndustry() {
      if (!this.new_industry?.trim()) return;
      this.brand.ICP.industry.push(this.new_industry.trim());
      this.new_industry = "";
    },
    addRole() {
      if (!this.new_role?.trim()) return;
      this.brand.ICP.roles.push(this.new_role.trim());
      this.new_role = "";
    },
    addPainPoint() {
      if (!this.new_painPoint?.trim()) return;
      this.brand.ICP.painPoints.push(this.new_painPoint.trim());
      this.new_painPoint = "";
    },
    addHow() {
      if (!this.new_how?.trim()) return;
      this.brand.goldenCircle.how.push(this.new_how.trim());
      this.new_how = "";
    },
    addWhat() {
      if (!this.new_what?.trim()) return;
      this.brand.goldenCircle.what.push(this.new_what.trim());
      this.new_what = "";
    },
    addProductService() {
      if (!this.new_product_services?.trim()) return;
      if (!this.brand.products_services) this.brand.products_services = [];
      this.brand.products_services.push(this.new_product_services.trim());
      this.new_product_services = "";
    },
    addUsp() {
      if (!this.new_usp?.trim()) return;
      if (!this.brand.USP) this.brand.USP = [];
      this.brand.USP.push(this.new_usp.trim());
      this.new_usp = "";
    },
    addRejection() {
      if (!this.new_rejection?.trim()) return;
      if (!this.brand.common_rejections) this.brand.common_rejections = [];
      this.brand.common_rejections.push(this.new_rejection.trim());
      this.new_rejection = "";
    },
    async analyzeColors() {
      this.analyzing_colors = true;
      const analyzeColors = httpsCallable(functions, "analyzeColors");
      const res = await analyzeColors({
        website: this.brand.website,
      });
      this.analyzing_colors = false;
      this.branding.color = { ...res.data };
      this.$store.commit("setNotification", {
        show: true,
        text: "Colors analyzed successfully",
        type: "primary",
        icon: "",
      });
    },
    async setPrimary(c) {
      await updateDoc(doc(db, "workspaces", this.workspace.uid), {
        "branding.color.primary": c,
      });
      this.branding.color.primary = c;
      this.$store.commit("setNotification", {
        show: true,
        text: "Primary color updated",
        type: "primary",
        icon: "",
      });
    },
    async setBackground(c) {
      await updateDoc(doc(db, "workspaces", this.workspace.uid), {
        "branding.color.background": c,
      });
      this.branding.color.background = c;
    },
    async setButton(c) {
      await updateDoc(doc(db, "workspaces", this.workspace.uid), {
        "branding.color.button": c,
      });
      this.branding.color.button = c;
    },
    async setText(c) {
      await updateDoc(doc(db, "workspaces", this.workspace.uid), {
        "branding.color.text": c,
      });
      this.branding.color.text = c;
    },
    async setAccent(c) {
      await updateDoc(doc(db, "workspaces", this.workspace.uid), {
        "branding.color.accent": c,
      });
      this.branding.color.accent = c;
    },
    async onDarkLogoChange() {
      var files = this.$refs.darkLogoRef.files;
      const upload = files[0];
      var fileName = upload.name;
      var fileExtension = fileName.split(".").pop();
      const storage = getStorage();
      const storageRef = ref(
        storage,
        `${this.workspace.uid}/brand/logo_dark.${fileExtension}`,
      );

      await uploadBytesResumable(storageRef, upload);

      const downloadURL = await getDownloadURL(storageRef);
      const updateRef = doc(db, "workspaces", this.workspace.uid);

      await updateDoc(updateRef, {
        "branding.logo.dark": downloadURL,
      });

      this.branding.logo.dark = downloadURL;

      this.$store.commit("setNotification", {
        show: true,
        text: "Dark logo added!",
        type: "primary",
        icon: "",
      });
    },
    async onLightLogoChange() {
      var files = this.$refs.lightLogoRef.files;
      const upload = files[0];
      var fileName = upload.name;
      var fileExtension = fileName.split(".").pop();
      const storage = getStorage();
      const storageRef = ref(
        storage,
        `${this.userProfile.workspace}/brand/logo_light.${fileExtension}`,
      );

      await uploadBytesResumable(storageRef, upload);

      const downloadURL = await getDownloadURL(storageRef);
      const updateRef = doc(db, "workspaces", this.workspace.uid);

      await updateDoc(updateRef, {
        "branding.logo.light": downloadURL,
      });

      this.branding.logo.light = downloadURL;

      this.$store.commit("setNotification", {
        show: true,
        text: "Light logo added!",
        type: "primary",
        icon: "",
      });
    },

    async deleteAccount(id) {
      this.$store.commit("setLoading", { type: "general", val: true });

      const accountDelete = httpsCallable(functions, "accountDelete");
      const res = await accountDelete({
        id: id,
        workspace: this.workspace.uid,
      });
      console.log(res.data);

      this.$store.dispatch("fetchAccounts");
      this.$store.commit("setLoading", { type: "general", val: false });
    },
  },
};
</script>

<style lang="scss" scoped>
input[type="file"] {
  position: absolute;
  left: 0;
  top: 0;
  opacity: 0;
  width: 0.1px;
  height: 0.1px;
}

.brand-tab {
  text-transform: none !important;
  font-size: 10pt !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  min-width: auto !important;
  padding: 0 16px !important;
}

.import-post-row {
  border-bottom: 1px solid hsl(214.3 31.8% 91.4%);
  transition: background-color 0.1s ease;
}

.import-post-row:hover {
  background-color: hsl(210 40% 98%);
}

.import-post-row--selected {
  background-color: hsl(210 40% 97%);
}

.import-post-text {
  font-size: 10pt;
  line-height: 1.5;
  color: #09090b;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}
</style>
